/**
 * Категории ленты.
 *
 * Сфера — верхний уровень навигации покупателя: «где я трачу деньги». Пока
 * список жил в коде, добавление категории означало релиз.
 *
 * Ключ задаётся один раз: он уезжает в адрес страницы («/?sphere=drinks») и в
 * аналитику, и менять его значит ломать чужие ссылки. Приметы — слова, по
 * которым свободная категория бренда попадает в сферу сама.
 */
import type { Sphere } from '@allswipes/client';
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Icon, Skeleton, num, type IconName } from '@allswipes/ui';
import { useState } from 'react';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { LangTabs, setTr, tr, type ContentLang } from '../components/LangTabs';
import { useAdmin } from '../session';
import { useToast } from '../toast';

const EMPTY: Partial<Sphere> = {
  key: '',
  label: '',
  icon: 'building',
  matches: [],
  position: 100,
  enabled: true,
};

export function SpheresScreen() {
  const data = useResource(() => api.adminSpheres(), []);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [editing, setEditing] = useState<{ sphere: Partial<Sphere>; isNew: boolean } | null>(null);

  const toggle = async (sphere: Sphere) => {
    try {
      await api.adminSaveSphere({ ...sphere, enabled: !sphere.enabled }, false);
      toast(sphere.enabled ? 'Категория скрыта из ленты' : 'Категория показывается');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  const remove = async (sphere: Sphere) => {
    if (!window.confirm(`Удалить категорию «${sphere.label}»?`)) return;
    try {
      await api.adminDeleteSphere(sphere.key);
      toast('Категория удалена');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    }
  };

  return (
    <Layout
      title="Категории"
      subtitle="разрезы ленты покупателя: «где я трачу деньги»"
      action={
        canModerate ? (
          <Button variant="pri" icon="plus" onClick={() => setEditing({ sphere: { ...EMPTY }, isNew: true })}>
            Новая категория
          </Button>
        ) : undefined
      }
    >
      <ReadOnlyNote />

      {data.loading && <Skeleton count={5} height={48} />}
      {data.data && data.data.spheres.length === 0 && <Empty icon="building" title="Категорий нет" />}

      {data.data && data.data.spheres.length > 0 && (
        <Card>
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Категория</th>
                  <th>Ключ</th>
                  <th>Приметы</th>
                  <th>Брендов</th>
                  <th>Порядок</th>
                  {canModerate && <th />}
                </tr>
              </thead>
              <tbody>
                {data.data.spheres.map((sphere) => (
                  <tr key={sphere.key}>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <Icon name={sphere.icon as IconName} size={15} />
                        <span>{sphere.label}</span>
                        {sphere.builtin && <Badge>встроенная</Badge>}
                        {!sphere.enabled && <Badge tone="warn">скрыта</Badge>}
                      </div>
                    </td>
                    <td className="mono faint">{sphere.key}</td>
                    <td className="faint" style={{ fontSize: 12 }}>
                      {sphere.matches && sphere.matches.length > 0
                        ? sphere.matches.join(' · ')
                        : '— только вручную'}
                    </td>
                    <td>{num(sphere.brands ?? 0)}</td>
                    <td className="mono faint">{sphere.position}</td>
                    {canModerate && (
                      <td>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <Button size="sm" onClick={() => void toggle(sphere)}>
                            {sphere.enabled ? 'Скрыть' : 'Показать'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setEditing({ sphere, isNew: false })}
                          >
                            Править
                          </Button>
                          {!sphere.builtin && (
                            <Button size="sm" variant="danger" onClick={() => void remove(sphere)}>
                              Удалить
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && (
        <SphereDialog
          sphere={editing.sphere}
          isNew={editing.isNew}
          icons={data.data?.icons ?? []}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </Layout>
  );
}

function SphereDialog({
  sphere,
  isNew,
  icons,
  onClose,
  onDone,
}: {
  sphere: Partial<Sphere>;
  isNew: boolean;
  icons: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(sphere);
  const [matches, setMatches] = useState((sphere.matches ?? []).join(', '));
  const [lang, setLang] = useState<ContentLang>('ru');
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<Sphere>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.adminSaveSphere(
        {
          ...draft,
          key: (draft.key ?? '').trim().toLowerCase(),
          matches: matches
            .split(',')
            .map((m) => m.trim())
            .filter(Boolean),
        } as Sphere,
        isNew,
      );
      toast(isNew ? 'Категория создана' : 'Сохранено');
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ovl" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 2 }}>
          <span className="h2">{isNew ? 'Новая категория' : draft.label}</span>
          <span className="spacer" />
          <LangTabs lang={lang} onChange={setLang} i18n={draft.i18n} />
        </div>
        <p className="sub" style={{ margin: '6px 0 14px' }}>
          Ключ задаётся один раз: он уезжает в адрес страницы и в аналитику. Приметы — слова, по
          которым свободная категория бренда попадёт сюда сама; без них категорию можно будет
          назначать только вручную на карточке бренда.
        </p>

        <div className="fgrid">
          <label className="field">
            <span className="lab">Название</span>
            <input
              className="input"
              value={tr(draft.i18n, lang, 'label', draft.label ?? '')}
              onChange={(e) =>
                lang === 'ru'
                  ? set({ label: e.target.value })
                  : set({ i18n: setTr(draft.i18n, lang, 'label', e.target.value) })
              }
            />
          </label>
          <label className="field">
            <span className="lab">Ключ</span>
            <input
              className="input mono"
              value={draft.key ?? ''}
              disabled={!isNew}
              placeholder="например, books"
              onChange={(e) => set({ key: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="lab">Иконка</span>
            <select
              className="select"
              value={draft.icon ?? 'building'}
              onChange={(e) => set({ icon: e.target.value })}
            >
              {icons.map((icon) => (
                <option key={icon} value={icon}>
                  {icon}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="lab">Порядок</span>
            <input
              className="input mono"
              type="number"
              value={draft.position ?? 100}
              onChange={(e) => set({ position: Number(e.target.value) })}
            />
          </label>
        </div>

        <label className="field">
          <span className="lab">Приметы — через запятую</span>
          <input
            className="input"
            value={matches}
            placeholder="книг, канцеляр, учебник"
            onChange={(e) => setMatches(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="lab">Показывать в ленте</span>
          <select
            className="select"
            value={draft.enabled ? '1' : '0'}
            onChange={(e) => set({ enabled: e.target.value === '1' })}
          >
            <option value="1">да</option>
            <option value="0">нет</option>
          </select>
        </label>

        <div className="row" style={{ marginTop: 14, gap: 8 }}>
          <Button onClick={onClose}>Отмена</Button>
          <span className="spacer" />
          <Button
            variant="pri"
            disabled={busy || !(draft.label ?? '').trim() || !(draft.key ?? '').trim()}
            onClick={submit}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </div>
  );
}
